import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import useTableSort from "../hooks/useTableSort";
import "./ReportsSectionCompact.css";
import {
  downloadBlob,
  getMaintenanceReportLogs,
  getMaintenanceReportStaff,
  getMaintenanceReportSummary,
  getMaintenanceReportTasks,
  getMaintenanceReportTrends,
} from "../api/reports";
import { REPORT_HELP } from "../constants/reportHelpContent";
import { MAINTENANCE_REPORT_TOOLTIPS } from "../constants/reportTooltips";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { getSalonTimezone } from "../utils/salonTimezone";

const formatDateISO = (value, timeZone) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(value);
    } catch (err) {
      // Fall back to local date below.
    }
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatPct = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(1)}%`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return digits > 0 ? (0).toFixed(digits) : "0";
  return num.toFixed(digits);
};

const formatHours = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${num.toFixed(2)}h`;
};

const csvEscape = (value) => {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export default function MaintenanceCleaningAnalyticsReport() {
  const navigate = useNavigate();
  const { currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.MAINTENANCE_CLEANING_ANALYTICS;
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const today = useMemo(() => formatDateISO(new Date(), salonTimezone), [salonTimezone]);
  const defaultStart = useMemo(() => formatDateISO(addDays(new Date(), -29), salonTimezone), [salonTimezone]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [staffId, setStaffId] = useState("");
  const [category, setCategory] = useState("");
  const [frequencyType, setFrequencyType] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [taskId, setTaskId] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [taskRows, setTaskRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [logsRows, setLogsRows] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState("");
  const [logsOpen, setLogsOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const filters = summary?.filters || {};
  const statusOptions = filters?.status_options || [];
  const frequencyOptions = filters?.frequency_options || [];
  const staffOptions = filters?.staff_options || [];
  const categoryOptions = filters?.category_options || [];
  const taskOptions = filters?.task_options || [];
  const breakdowns = summary?.breakdowns || {};
  const kpis = summary?.kpis || {};

  const baseParams = useMemo(() => {
    const params = {
      startDate,
      endDate,
      status: statusFilter,
      includeInactive,
    };
    if (staffId) params.staffId = Number(staffId);
    if (category) params.category = category;
    if (frequencyType) params.frequencyType = frequencyType;
    if (taskId) params.taskId = Number(taskId);
    return params;
  }, [startDate, endDate, statusFilter, includeInactive, staffId, category, frequencyType, taskId]);

  const bucket = useMemo(() => {
    if (!startDate || !endDate) return "day";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const dayDiff = Math.floor((end.getTime() - start.getTime()) / 86400000);
    return dayDiff > 60 ? "week" : "day";
  }, [startDate, endDate]);

  const dateRangeError = useMemo(() => {
    if (!startDate || !endDate) return "Start and end dates are required.";
    if (endDate < startDate) return "End date cannot be before start date.";
    return "";
  }, [startDate, endDate]);

  const loadMain = useCallback(async () => {
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [summaryRes, trendsRes, tasksRes, staffRes] = await Promise.all([
        getMaintenanceReportSummary(baseParams),
        getMaintenanceReportTrends({ ...baseParams, bucket }),
        getMaintenanceReportTasks(baseParams),
        getMaintenanceReportStaff(baseParams),
      ]);
      setSummary(summaryRes || null);
      setTrends(Array.isArray(trendsRes?.rows) ? trendsRes.rows : []);
      setTaskRows(Array.isArray(tasksRes?.rows) ? tasksRes.rows : []);
      setStaffRows(Array.isArray(staffRes?.rows) ? staffRes.rows : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load maintenance analytics.");
      setSummary(null);
      setTrends([]);
      setTaskRows([]);
      setStaffRows([]);
    } finally {
      setLoading(false);
    }
  }, [baseParams, bucket, dateRangeError]);

  const loadLogs = useCallback(async () => {
    if (dateRangeError) return;
    setLoadingLogs(true);
    try {
      const res = await getMaintenanceReportLogs({
        ...baseParams,
        page,
        pageSize,
      });
      setLogsRows(Array.isArray(res?.rows) ? res.rows : []);
      setLogsTotal(Number(res?.total || 0));
    } catch (err) {
      setLogsRows([]);
      setLogsTotal(0);
    } finally {
      setLoadingLogs(false);
    }
  }, [baseParams, page, pageSize, dateRangeError]);

  useEffect(() => {
    loadMain();
    setPage(1);
  }, [loadMain, refreshKey]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs, refreshKey]);

  const taskSort = useTableSort({
    rows: taskRows,
    columns: {
      task_name: { type: "text", getValue: (row) => row?.task_name },
      expected: { type: "number", getValue: (row) => row?.expected },
      completed: { type: "number", getValue: (row) => row?.completed },
      on_time_pct: { type: "number", getValue: (row) => row?.on_time_pct || 0 },
      overdue_count: { type: "number", getValue: (row) => row?.overdue_count },
      missed_count: { type: "number", getValue: (row) => row?.missed_count },
    },
    defaultSortBy: "overdue_count",
    fallbackKey: "task_name",
  });

  const staffSort = useTableSort({
    rows: staffRows,
    columns: {
      staff_name: { type: "text", getValue: (row) => row?.staff_name },
      expected: { type: "number", getValue: (row) => row?.expected },
      completed: { type: "number", getValue: (row) => row?.completed },
      on_time_pct: { type: "number", getValue: (row) => row?.on_time_pct || 0 },
      late_pct: { type: "number", getValue: (row) => row?.late_pct || 0 },
      missed_count: { type: "number", getValue: (row) => row?.missed_count },
      longest_on_time_streak: { type: "number", getValue: (row) => row?.longest_on_time_streak || 0 },
    },
    defaultSortBy: "on_time_pct",
    fallbackKey: "staff_name",
  });

  const trendMax = useMemo(() => {
    const values = trends.map((row) => Math.max(Number(row?.on_time_count || 0), Number(row?.late_count || 0), Number(row?.missed_count || 0)));
    return Math.max(1, ...values);
  }, [trends]);

  const logsHasPrev = page > 1;
  const logsHasNext = page * pageSize < logsTotal;

  const runReport = () => {
    setPage(1);
    setRefreshKey((prev) => prev + 1);
  };

  const resetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(today);
    setStaffId("");
    setCategory("");
    setFrequencyType("");
    setStatusFilter("ALL");
    setTaskId("");
    setIncludeInactive(false);
    setPage(1);
    setRefreshKey((prev) => prev + 1);
  };

  const exportLogsCsv = async () => {
    const rows = [];
    let currentPage = 1;
    const loopSize = 200;
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const res = await getMaintenanceReportLogs({
        ...baseParams,
        page: currentPage,
        pageSize: loopSize,
      });
      const nextRows = Array.isArray(res?.rows) ? res.rows : [];
      rows.push(...nextRows);
      if (rows.length >= Number(res?.total || 0) || nextRows.length < loopSize) break;
      currentPage += 1;
    }
    const header = ["Timestamp (Salon TZ)", "Task", "Staff", "Status", "Hours Late", "Completed By", "Note"];
    const lines = [header.join(",")];
    rows.forEach((row) => {
      lines.push(
        [
          csvEscape(row.timestamp_local),
          csvEscape(row.task),
          csvEscape(row.staff_name || ""),
          csvEscape(row.status_label || ""),
          csvEscape(row.hours_late),
          csvEscape(row.completed_by_username || ""),
          csvEscape(row.note || ""),
        ].join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `maintenance_logs_${startDate}_to_${endDate}.csv`);
  };

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Maintenance & Cleaning Analytics" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="maintenance-analytics-overview" className={getSectionClassName("maintenance-analytics-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">{error}</div> : null}
            <div id="maintenance-analytics-filters" className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName("maintenance-analytics-filters")}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div><label className={LABEL_TEXT}>Date From</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${FIELD_CLASS} w-full`} /></div>
                <div><label className={LABEL_TEXT}>Date To</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`${FIELD_CLASS} w-full`} /></div>
                <div><div className="flex items-center gap-1.5"><label className={LABEL_TEXT}>Assigned Staff</label><InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.staff} label="Assigned Staff" /></div><select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={`${FIELD_CLASS} w-full`}><option value="">All</option>{staffOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></div>
                <div><div className="flex items-center gap-1.5"><label className={LABEL_TEXT}>Category</label><InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.category} label="Category" /></div><select value={category} onChange={(e) => setCategory(e.target.value)} className={`${FIELD_CLASS} w-full`}><option value="">All</option>{categoryOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></div>
                <div><div className="flex items-center gap-1.5"><label className={LABEL_TEXT}>Frequency Type</label><InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.frequency_type} label="Frequency Type" /></div><select value={frequencyType} onChange={(e) => setFrequencyType(e.target.value)} className={`${FIELD_CLASS} w-full`}><option value="">All</option>{frequencyOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
                <div><div className="flex items-center gap-1.5"><label className={LABEL_TEXT}>Status</label><InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.status} label="Status" /></div><select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${FIELD_CLASS} w-full`}>{statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
                <div><div className="flex items-center gap-1.5"><label className={LABEL_TEXT}>Task</label><InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.task} label="Task" /></div><select value={taskId} onChange={(e) => setTaskId(e.target.value)} className={`${FIELD_CLASS} w-full`}><option value="">All</option>{taskOptions.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}</select></div>
                <div className="flex items-center gap-2 pt-8"><input id="maintenance-include-inactive" type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} /><label htmlFor="maintenance-include-inactive" className="text-sm text-slate-700 flex items-center gap-1.5">Include inactive tasks <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.include_inactive} label="Include inactive tasks" /></label></div>
              </div>
              {dateRangeError ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">{dateRangeError}</div> : null}
              <div className="flex flex-wrap items-center gap-3"><div className="text-xs text-slate-500">Salon timezone: {summary?.meta?.timezone || salonTimezone}</div><div className="ml-auto flex flex-wrap gap-3"><button type="button" onClick={runReport} className={`${BTN_PRIMARY} min-w-[140px]`} disabled={loading}>{loading ? "Loading..." : "Run Report"}</button><button type="button" onClick={resetFilters} className={`${BTN_OUTLINE} min-w-[140px]`}>Reset</button><button type="button" onClick={() => navigate("/manager/reports")} className={`${BTN_OUTLINE} min-w-[140px]`}>Back to Reports</button></div></div>
            </div>
            <div id="maintenance-analytics-kpis" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">{[
              { label: "Total Completions", value: formatNumber(kpis.total_completions || 0, 0) },
              { label: "Completion Rate", value: formatPct(kpis.completion_rate_pct), tip: MAINTENANCE_REPORT_TOOLTIPS.completion_rate },
              { label: "Overdue Rate", value: formatPct(kpis.overdue_rate_pct) },
              { label: "Avg Time Late", value: formatHours(kpis.avg_time_late_hours) },
              { label: "Most Overdue Task", value: kpis?.most_overdue_task ? `${kpis.most_overdue_task.task_name} (${kpis.most_overdue_task.overdue_total})` : "--" },
              { label: "Top Performer Staff", value: kpis?.top_performer_staff ? `${kpis.top_performer_staff.staff_name} (${formatPct(kpis.top_performer_staff.on_time_pct)})` : "--" },
              { label: "Tasks At Risk", value: formatNumber(kpis.tasks_at_risk || 0, 0) },
              { label: "Consistency Score", value: formatNumber(kpis.consistency_score || 0, 1), tip: MAINTENANCE_REPORT_TOOLTIPS.consistency_score },
            ].map((card) => <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5">{card.label}{card.tip ? <InfoTooltip text={card.tip} label={card.label} /> : null}</div><div className="mt-2 text-xl font-semibold text-slate-900">{card.value}</div></div>)}</div>
            <div
              id="maintenance-analytics-trends"
              className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                "maintenance-analytics-trends"
              )}`}
            >
              <div className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
                Status Trends Over Time{" "}
                <InfoTooltip
                  text={MAINTENANCE_REPORT_TOOLTIPS.section_trends}
                  label="Status Trends Over Time"
                />
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="h-56 flex items-end gap-2">
                  {trends.length ? (
                    trends.map((row, index) => {
                      const label = row?.label || "";
                      const onTime = Number(row?.on_time_count || 0);
                      const late = Number(row?.late_count || 0);
                      const missed = Number(row?.missed_count || 0);
                      const showLabel =
                        trends.length <= 10 ||
                        index % Math.ceil(trends.length / 8) === 0;
                      return (
                        <div
                          key={`${label}-${index}`}
                          className="flex-1 min-w-0 flex flex-col items-center gap-1"
                        >
                          <div className="w-full h-40 flex items-end justify-center gap-0.5">
                            <div
                              className="w-1/3 rounded-t bg-emerald-400"
                              style={{ height: `${Math.max(2, (onTime / trendMax) * 100)}%` }}
                              title={`On-time: ${onTime}`}
                            />
                            <div
                              className="w-1/3 rounded-t bg-amber-400"
                              style={{ height: `${Math.max(2, (late / trendMax) * 100)}%` }}
                              title={`Late: ${late}`}
                            />
                            <div
                              className="w-1/3 rounded-t bg-rose-400"
                              style={{ height: `${Math.max(2, (missed / trendMax) * 100)}%` }}
                              title={`Missed: ${missed}`}
                            />
                          </div>
                          <div className="text-[11px] text-slate-500 text-center min-h-[1rem]">
                            {showLabel ? label : ""}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full grid place-items-center text-slate-500 text-sm">
                      {loading ? "Loading..." : "No trend data in this range."}
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> On-time
                    <InfoTooltip
                      text={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition}
                      label="On-time"
                    />
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Late
                    <InfoTooltip
                      text={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition}
                      label="Late"
                    />
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2.5 w-2.5 rounded-full bg-rose-400" /> Missed
                    <InfoTooltip
                      text={MAINTENANCE_REPORT_TOOLTIPS.missed_definition}
                      label="Missed"
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div
                id="maintenance-analytics-frequency"
                className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 flex items-center gap-2">
                  Completion by Frequency Type{" "}
                  <InfoTooltip
                    text={MAINTENANCE_REPORT_TOOLTIPS.section_frequency}
                    label="Completion by Frequency Type"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Frequency</th>
                        <th className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1">
                            Expected
                            <InfoTooltip
                              text={MAINTENANCE_REPORT_TOOLTIPS.expected_occurrences}
                              label="Expected occurrences"
                            />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1">
                            On-time
                            <InfoTooltip
                              text={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition}
                              label="On-time definition"
                            />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1">
                            Late
                            <InfoTooltip
                              text={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition}
                              label="Late definition"
                            />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1">
                            Missed
                            <InfoTooltip
                              text={MAINTENANCE_REPORT_TOOLTIPS.missed_definition}
                              label="Missed definition"
                            />
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1">
                            Rate
                            <InfoTooltip
                              text={MAINTENANCE_REPORT_TOOLTIPS.completion_rate}
                              label="Completion rate"
                            />
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdowns?.by_frequency || []).map((row) => (
                        <tr key={row.frequency_type} className="border-t border-slate-100">
                          <td className="px-3 py-2 text-slate-800">{row.frequency_label}</td>
                          <td className="px-3 py-2 text-right">
                            {formatNumber(row.expected_occurrences, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatNumber(row.completed_on_time, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatNumber(row.completed_late, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatNumber(row.missed, 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatPct(row.completion_rate_pct)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-600 flex items-center gap-1.5">
                  Event-based frequencies may be excluded from expected/missed totals.
                  <InfoTooltip
                    text={MAINTENANCE_REPORT_TOOLTIPS.event_based}
                    label="Event-based frequency note"
                  />
                </div>
              </div>
              <div id="maintenance-analytics-day-of-week" className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"><div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 flex items-center gap-2">Day Of Week Completions <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.section_day_of_week} label="Day Of Week Completions" /></div><div className="p-4 space-y-2">{(breakdowns?.by_day_of_week_completions || []).map((row) => <div key={row.day_of_week} className="flex items-center gap-3"><div className="w-24 text-sm text-slate-700">{row.day_of_week}</div><div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${Math.min(100, ((Number(row.completion_count || 0) / Math.max(1, ...((breakdowns?.by_day_of_week_completions || []).map((item) => Number(item.completion_count || 0))))) * 100))}%` }} /></div><div className="w-10 text-right text-sm text-slate-600">{formatNumber(row.completion_count, 0)}</div></div>)}</div></div>
            </div>
            <div id="maintenance-analytics-tasks" className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName("maintenance-analytics-tasks")}`}><div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 flex items-center gap-2">Task Leaderboard <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.section_tasks} label="Task Leaderboard" /></div><div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr><SortableTh label="Task" sortKey="task_name" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-left" /><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Frequency</th><th className="px-3 py-2 text-left">Assigned Staff</th><SortableTh label="Expected" sortKey="expected" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.expected_occurrences} tooltipLabel="Expected occurrences" /><th className="px-3 py-2 text-right">Completed</th><SortableTh label="On-time %" sortKey="on_time_pct" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition} tooltipLabel="On-time vs late definition" /><SortableTh label="Overdue" sortKey="overdue_count" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" /><SortableTh label="Missed" sortKey="missed_count" sortState={taskSort.sortState} onSort={taskSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.missed_definition} tooltipLabel="Missed definition" /><th className="px-3 py-2 text-right">Avg Days Late</th></tr></thead><tbody>{taskSort.sortedRows.map((row) => <tr key={row.task_id} className="border-t border-slate-100"><td className="px-3 py-2 text-slate-800">{row.task_name}</td><td className="px-3 py-2 text-slate-700">{row.category}</td><td className="px-3 py-2 text-slate-700">{row.frequency}</td><td className="px-3 py-2 text-slate-700">{row.assigned_staff}</td><td className="px-3 py-2 text-right">{formatNumber(row.expected, 0)}</td><td className="px-3 py-2 text-right">{formatNumber(row.completed, 0)}</td><td className="px-3 py-2 text-right">{formatPct(row.on_time_pct)}</td><td className="px-3 py-2 text-right">{formatNumber(row.overdue_count, 0)}</td><td className="px-3 py-2 text-right">{formatNumber(row.missed_count, 0)}</td><td className="px-3 py-2 text-right">{formatNumber(row.avg_days_late, 2)}</td></tr>)}{!taskSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={10}>{loading ? "Loading..." : "No task rows for this range."}</td></tr> : null}</tbody></table></div></div>
            <div id="maintenance-analytics-staff" className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName("maintenance-analytics-staff")}`}><div className="px-4 py-3 border-b border-slate-200 font-semibold text-slate-900 flex items-center gap-2">Staff Performance <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.section_staff} label="Staff Performance" /></div><div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr><SortableTh label="Staff" sortKey="staff_name" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-left" /><th className="px-3 py-2 text-right"><span className="inline-flex items-center gap-1">Expected <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.expected_occurrences} label="Expected occurrences" /></span></th><th className="px-3 py-2 text-right">Completed</th><SortableTh label="On-time %" sortKey="on_time_pct" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition} tooltipLabel="On-time vs late definition" /><SortableTh label="Late %" sortKey="late_pct" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.on_time_late_definition} tooltipLabel="On-time vs late definition" /><SortableTh label="Missed" sortKey="missed_count" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" tooltip={MAINTENANCE_REPORT_TOOLTIPS.missed_definition} tooltipLabel="Missed definition" /><SortableTh label="Streak" sortKey="longest_on_time_streak" sortState={staffSort.sortState} onSort={staffSort.handleSort} className="px-3 py-2 text-right" align="right" /><th className="px-3 py-2 text-right">Last Completion</th></tr></thead><tbody>{staffSort.sortedRows.map((row, idx) => <tr key={`${row.staff_id || "unassigned"}-${idx}`} className="border-t border-slate-100"><td className="px-3 py-2 text-slate-800">{row.staff_name}</td><td className="px-3 py-2 text-right">{formatNumber(row.expected, 0)}</td><td className="px-3 py-2 text-right">{formatNumber(row.completed, 0)}</td><td className="px-3 py-2 text-right">{formatPct(row.on_time_pct)}</td><td className="px-3 py-2 text-right">{formatPct(row.late_pct)}</td><td className="px-3 py-2 text-right">{formatNumber(row.missed_count, 0)}</td><td className="px-3 py-2 text-right">{formatNumber(row.longest_on_time_streak, 0)}</td><td className="px-3 py-2 text-right">{row.last_completion_at || "--"}</td></tr>)}{!staffSort.sortedRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={8}>{loading ? "Loading..." : "No staff rows for this range."}</td></tr> : null}</tbody></table></div></div>
            <div id="maintenance-analytics-logs" className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName("maintenance-analytics-logs")}`}><div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3"><div className="font-semibold text-slate-900 flex items-center gap-2">Audit & Exceptions <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.section_logs} label="Audit & Exceptions" /></div><div className="flex gap-2"><button type="button" onClick={() => setLogsOpen((prev) => !prev)} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">{logsOpen ? "Collapse" : "Expand"}</button><button type="button" onClick={exportLogsCsv} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" disabled={loadingLogs}><span className="inline-flex items-center gap-1">Export CSV <InfoTooltip text={MAINTENANCE_REPORT_TOOLTIPS.export_logs} label="Export CSV" /></span></button></div></div>{logsOpen ? <div className="space-y-3 p-4"><div className="text-xs text-slate-500">Displayed in salon timezone: {summary?.meta?.timezone || salonTimezone}</div><div className="overflow-x-auto"><table className="min-w-full text-sm"><thead className="bg-slate-50 text-slate-600"><tr><th className="px-3 py-2 text-left">Timestamp</th><th className="px-3 py-2 text-left">Task</th><th className="px-3 py-2 text-left">Staff</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Hours Late</th><th className="px-3 py-2 text-left">Completed By</th><th className="px-3 py-2 text-left">Note</th></tr></thead><tbody>{logsRows.map((row) => <tr key={row.id} className="border-t border-slate-100"><td className="px-3 py-2 text-slate-700">{row.timestamp_local}</td><td className="px-3 py-2 text-slate-800">{row.task}</td><td className="px-3 py-2 text-slate-700">{row.staff_name || "--"}</td><td className="px-3 py-2 text-slate-700">{row.status_label}</td><td className="px-3 py-2 text-slate-700">{formatNumber(row.hours_late, 2)}</td><td className="px-3 py-2 text-slate-700">{row.completed_by_username || "--"}</td><td className="px-3 py-2 text-slate-700">{row.note || "--"}</td></tr>)}{!logsRows.length ? <tr><td className="px-3 py-6 text-slate-500" colSpan={7}>{loadingLogs ? "Loading logs..." : "No completion logs for this range."}</td></tr> : null}</tbody></table></div><div className="flex items-center justify-end gap-2"><button type="button" onClick={() => setPage((prev) => Math.max(1, prev - 1))} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" disabled={!logsHasPrev || loadingLogs}>Previous</button><span className="text-xs text-slate-500">Page {page}</span><button type="button" onClick={() => setPage((prev) => prev + 1)} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" disabled={!logsHasNext || loadingLogs}>Next</button></div></div> : null}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
